<template>
    <div class="min-h-screen overflow-x-hidden bg-[#FFFAF6] font-sans text-[#24120A]" :lang="lang">
        <section class="bg-luuqa text-white">
            <div class="mx-auto max-w-6xl px-5 pb-14 pt-6 sm:px-8">
                <nav class="flex items-center justify-between gap-3 text-sm font-medium">
                    <a href="/" aria-label="Heesify"><img src="@/assets/logo-full.svg" alt="Heesify" class="h-9 w-auto brightness-0 invert sm:h-10" /></a>
                    <div class="flex items-center gap-4">
                        <a href="#get" class="hidden opacity-90 hover:opacity-100 sm:inline">{{ t.getApp }}</a>
                        <div class="flex rounded-full bg-white/20 p-1 text-xs font-semibold" role="group" :aria-label="t.language">
                            <a
                                v-for="option in languages"
                                :key="option.code"
                                :href="option.href"
                                :hreflang="option.code"
                                :aria-current="option.code === lang ? 'true' : undefined"
                                class="rounded-full px-3 py-1.5"
                                :class="option.code === lang ? 'bg-white text-luuqa' : 'text-white'"
                            >
                                {{ option.label }}
                            </a>
                        </div>
                    </div>
                </nav>

                <div class="grid items-center gap-12 pt-12 lg:grid-cols-[1fr_1.15fr] lg:pt-16">
                    <div>
                        <h1 class="sr-only">Luuqa</h1>
                        <LuuqaLogo class="h-16 w-auto sm:h-20" />
                        <p class="mt-7 text-4xl font-bold leading-tight tracking-tight sm:text-5xl">{{ t.headline }}</p>
                        <p class="mt-4 max-w-md text-lg text-white/90">{{ t.intro }}</p>
                        <StoreButtons class="mt-8" :stores="stores" :labels="t.stores" />
                        <p class="mt-4 text-sm text-white/80">{{ t.inside }}</p>
                    </div>

                    <div class="mx-auto flex w-full max-w-xl items-start justify-center">
                        <img
                            :src="`${cdn}/discover.webp`"
                            :alt="t.alts.discover"
                            width="720"
                            height="1565"
                            class="relative z-10 h-auto w-[56%] rounded-[2rem] border-4 border-white/90"
                        />
                        <img
                            :src="`${cdn}/match.webp`"
                            :alt="t.alts.match"
                            width="720"
                            height="1565"
                            class="-ml-[12%] mt-[9%] h-auto w-[56%] rounded-[2rem] border-4 border-white/90"
                        />
                    </div>
                </div>
            </div>
        </section>

        <div ref="people" class="overflow-x-auto [scrollbar-width:none] sm:overflow-visible [&::-webkit-scrollbar]:hidden" tabindex="0" :aria-label="t.alts.people" role="group">
            <img
                :src="`${cdn}/people.webp`"
                :alt="t.alts.people"
                width="3200"
                height="500"
                class="h-40 w-auto max-w-none sm:h-auto sm:w-full sm:max-w-full"
                @load="centerPeople"
            />
        </div>

        <section class="mx-auto max-w-6xl px-5 py-16 sm:px-8">
            <p class="text-sm font-semibold uppercase tracking-wide text-luuqa">Guur, qoys iyo dhaqan</p>
            <h2 class="mt-2 max-w-2xl text-3xl font-bold tracking-tight">{{ t.madeFor }}</h2>
            <div class="mt-10 grid gap-4 md:grid-cols-3">
                <div v-for="point in t.points" :key="point[0]" class="rounded-2xl border border-[#F0DFD4] bg-white p-6">
                    <h3 class="text-lg font-semibold">{{ point[0] }}</h3>
                    <p class="mt-2 text-[#7A675D]">{{ point[1] }}</p>
                </div>
            </div>
        </section>

        <section class="mx-auto max-w-6xl space-y-20 px-5 pb-20 sm:px-8">
            <div v-for="(feature, index) in t.features" :key="feature.title" class="grid items-center gap-10 md:grid-cols-2">
                <div :class="index % 2 ? 'md:order-2' : ''">
                    <h2 class="text-3xl font-bold tracking-tight">{{ feature.title }}</h2>
                    <p class="mt-4 max-w-md text-lg text-[#7A675D]">{{ feature.body }}</p>
                </div>
                <div class="flex items-start justify-center gap-4" :class="index % 2 ? 'md:order-1' : ''">
                    <div v-for="shot in feature.shots" :key="shot" class="relative min-w-0 max-w-[16rem] flex-1">
                        <img
                            :src="`${cdn}/${shot}.webp`"
                            :alt="t.alts[shot]"
                            width="720"
                            height="1565"
                            loading="lazy"
                            class="h-auto w-full rounded-[2rem] border-4 border-[#24120A]"
                        />
                        <div
                            v-if="shot === 'chat'"
                            class="absolute -left-3 bottom-[24%] flex items-center gap-3 rounded-full border border-[#F0DFD4] bg-white px-4 py-2.5 sm:-left-8 sm:gap-4 sm:px-5 sm:py-3"
                            role="img"
                            :aria-label="t.reactions"
                        >
                            <svg v-for="icon in reactionIcons" :key="icon.name" viewBox="0 0 24 24" class="h-5 w-5 sm:h-6 sm:w-6" fill="none" :stroke="icon.color" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path v-for="line in icon.paths" :key="line" :d="line" />
                            </svg>
                        </div>
                        <div
                            v-if="shot === 'hidden'"
                            class="absolute -right-3 bottom-[34%] flex max-w-[85%] items-center gap-3 rounded-2xl border border-[#F0DFD4] bg-white p-3 sm:-right-10"
                        >
                            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-luuqa">
                                <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                                    <path d="m2 2 20 20" />
                                </svg>
                            </span>
                            <span class="text-sm font-semibold leading-snug">{{ t.hiddenCard }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="bg-[#24120A] text-white">
            <div class="mx-auto max-w-6xl px-5 py-16 sm:px-8">
                <h2 class="text-3xl font-bold tracking-tight">{{ t.safetyTitle }}</h2>
                <div class="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
                    <div v-for="item in t.safety" :key="item[0]">
                        <h3 class="font-semibold text-[#F8C4A8]">{{ item[0] }}</h3>
                        <p class="mt-2 text-white/80">{{ item[1] }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="mx-auto max-w-3xl px-5 py-16 sm:px-8">
            <h2 class="text-3xl font-bold tracking-tight">{{ t.faqTitle }}</h2>
            <div class="mt-8 divide-y divide-[#F0DFD4] border-y border-[#F0DFD4]">
                <div v-for="item in faq" :key="item[0]" class="py-5">
                    <h3 class="font-semibold">{{ item[0] }}</h3>
                    <p class="mt-2 text-[#7A675D]">{{ item[1] }}</p>
                </div>
            </div>
        </section>

        <section id="get" class="bg-luuqa text-white">
            <div class="mx-auto max-w-6xl px-5 py-16 text-center sm:px-8">
                <h2 class="text-3xl font-bold tracking-tight sm:text-4xl">{{ t.closing }}</h2>
                <p class="mx-auto mt-3 max-w-md text-lg text-white/90">{{ t.free }}</p>
                <StoreButtons class="mt-8 justify-center" :stores="stores" :labels="t.stores" />
            </div>
        </section>

        <footer class="mx-auto max-w-6xl px-5 py-8 text-sm text-[#7A675D] sm:px-8">
            <p>{{ t.sample }}</p>
            <a href="/" class="mt-2 inline-block font-medium text-luuqa">{{ t.home }}</a>
        </footer>
    </div>
</template>

<script>
import { h } from "vue";
import LuuqaLogo from "@/components/luuqa/LuuqaLogo.vue";

const StoreButtons = {
    props: ["stores", "labels"],
    render() {
        const button = (href, small, big) =>
            h(
                "a",
                {
                    href,
                    target: "_blank",
                    rel: "noopener",
                    class: "inline-flex min-w-[10.5rem] flex-col rounded-xl bg-white px-5 py-2.5 text-left text-[#24120A] transition hover:bg-[#FFF1E8]",
                },
                [h("span", { class: "text-xs" }, small), h("span", { class: "text-lg font-semibold leading-tight" }, big)]
            );
        return h("div", { class: "flex flex-wrap gap-3" }, [
            button(this.stores.apple, this.labels.apple, "App Store"),
            button(this.stores.google, this.labels.google, "Google Play"),
        ]);
    },
};

const WORDS = {
    en: {
        language: "Language",
        getApp: "Get the app",
        headline: "Find your person.",
        intro: "A Somali space for people serious about marriage, from Mogadishu to Minneapolis.",
        inside: "Inside the Heesify app. For adults 18 and over.",
        stores: { apple: "Download on the", google: "Get it on" },
        madeFor: "Made for how Somalis marry: with faith, family and intention.",
        points: [
            ["Built for marriage", "For people who mean it. Not for passing the time."],
            ["Private by design", "Your answers stay private, and nothing is shown until you choose."],
            ["Somali, worldwide", "Meet Somalis at home and across the diaspora, wherever you are."],
        ],
        features: [
            {
                title: "Know what matters first",
                body: "See prayer, marriage plans, children and education on every profile, before you ever say salaam.",
                shots: ["profile"],
            },
            {
                title: "Only when you're both interested",
                body: "Say Interested or Pass. A conversation opens only when you both say yes, so nobody is messaged out of nowhere. React to a message with a like, a dislike or a heart, and report anything that isn't right.",
                shots: ["matches", "chat"],
            },
            {
                title: "Your photos, your choice",
                body: "With a Luuqa plan you can hide your photos until you match. Others see a blur, and only your matches see you.",
                shots: ["hidden"],
            },
        ],
        safetyTitle: "Safety comes first",
        safety: [
            ["Reviewed by a person", "Every profile is checked by one of our team before anyone can see it."],
            ["Men and women only see each other", "Nobody is ever shown someone of their own gender."],
            ["Report and block", "Report or block anyone, any time. It's always free."],
            ["Photo verification", "Our reviewers can ask for a live photo to confirm someone is who they say they are."],
            ["Quiet notifications", "A new message alert never shows a name or the message on your lock screen."],
            ["Leave whenever you like", "Opting out deletes your Luuqa profile, photos and chats."],
        ],
        reactions: "Like, dislike, love or report a message",
        hiddenCard: "Hidden until you match",
        faqTitle: "Questions",
        closing: "Your person may already be here.",
        free: "Free to start, with a few swipes and messages each day. A Luuqa plan makes them unlimited.",
        sample: "Screens show sample profiles. The people pictured are illustrations, not members.",
        home: "Heesify, home of Somali music",
        alts: {
            people: "Illustrations of Somali men and women",
            discover: "A sample profile in Luuqa with Pass and Interested buttons",
            match: "The match screen in Luuqa, saying It's a match",
            profile: "A sample Luuqa profile with a bio and tags for prayer, children and marriage plans",
            matches: "The Luuqa conversations list with new matches",
            chat: "A respectful sample conversation in Somali about meeting the family",
            hidden: "A blurred profile photo with the words Photos show after you match",
        },
    },
    so: {
        language: "Luqadda",
        getApp: "Soo deji app-ka",
        headline: "Hel qofkaaga.",
        intro: "Meel Soomaaliyeed oo loogu talagalay dadka guurka dhab ka ah, laga bilaabo Muqdisho ilaa Minneapolis.",
        inside: "Gudaha app-ka Heesify. Dadka 18 jir iyo ka weyn.",
        stores: { apple: "Ka soo deji", google: "Ka hel" },
        madeFor: "Loo sameeyay sida Soomaalidu u guursato: diin, qoys iyo niyad wanaagsan.",
        points: [
            ["Guur ayaa loo dhisay", "Dadka runta ka ah. Ma aha waqti lumis."],
            ["Sir ayaa ku dhisan", "Jawaabahaagu way qarsoon yihiin, waxna lama muujiyo ilaa aad adigu doorato."],
            ["Soomaali adduunka oo dhan", "La kulan Soomaali dalka gudihiisa iyo qurba-joogta, meel kasta oo aad joogto."],
        ],
        features: [
            {
                title: "Ogow waxa muhiimka ah marka hore",
                body: "Profile kasta waxaad ku aragtaa salaadda, qorshaha guurka, carruurta iyo waxbarashada, intaadan salaan dirin.",
                shots: ["profile"],
            },
            {
                title: "Marka labadiinuba xiiseeyaan oo keliya",
                body: "Dooro Interested ama Pass. Wada-hadalku wuxuu furmaa marka labadiinuba haa dhahaan oo keliya, si aan cidna fariin lama filaan ah loogu soo dirin. Fariin waxaad ku jawaabi kartaa like, dislike ama wadne, wixii aan habboonaynna waad soo sheegi kartaa.",
                shots: ["matches", "chat"],
            },
            {
                title: "Sawiradaada, doorashadaada",
                body: "Qorshaha Luuqa waxaad ku qarin kartaa sawiradaada ilaa aad is-helaan. Dadka kale waxay arkaan sawir qarsoon, kuwa aad is-heshaan oo keliya ayaa ku arka.",
                shots: ["hidden"],
            },
        ],
        safetyTitle: "Badbaadadu waa koowaad",
        safety: [
            ["Qof ayaa hubiya", "Profile kasta waxaa hubiya qof kooxdeena ka mid ah ka hor inta aan cidna arkin."],
            ["Ragga iyo dumarka oo keliya ayaa is arka", "Cidna laguma tuso qof la mid ah jinsigiisa."],
            ["Soo sheeg oo xannib", "Qof kasta waad soo sheegi kartaa ama xannibi kartaa, wakhti kasta. Had iyo jeer waa bilaash."],
            ["Xaqiijinta sawirka", "Kooxdeenu waxay codsan karaan sawir toos ah si loo xaqiijiyo qofka inuu yahay cidda uu sheegayo."],
            ["Ogeysiisyo qarsoon", "Ogeysiiska fariinta cusub weligiis kuma muujiyo magac ama fariinta shaashadda qufulan."],
            ["Bax wakhti kasta", "Haddaad ka baxdo, waxaa la tirayaa profile-kaaga Luuqa, sawiradaada iyo wada-sheekaysiyadaada."],
        ],
        reactions: "Like, dislike, wadne ama soo sheeg fariin",
        hiddenCard: "Waa qarsoon tahay ilaa aad is-heshaan",
        faqTitle: "Su'aalo",
        closing: "Qofkaagu halkan ayuu joogi karaa.",
        free: "Waxaad ku bilaabi kartaa bilaash, maalin kasta dhowr swipe iyo fariimo. Qorshaha Luuqa wuxuu ka dhigaa kuwo aan xad lahayn.",
        sample: "Shaashadahu waxay muujinayaan tusaale profile. Dadka sawiran waa sawir-gacmeed, ma aha xubno.",
        home: "Heesify, hoyga muusikada Soomaaliyeed",
        alts: {
            people: "Sawirro-gacmeed ah rag iyo dumar Soomaaliyeed",
            discover: "Tusaale profile ah oo Luuqa ku jira, leh badhamada Pass iyo Interested",
            match: "Shaashadda Luuqa ee labada qof is-heleen",
            profile: "Tusaale profile ah oo leh warbixin kooban iyo calaamado salaadda, carruurta iyo qorshaha guurka",
            matches: "Liiska wada-sheekaysiga Luuqa",
            chat: "Tusaale wada-sheekaysi ixtiraam leh oo Soomaali ah oo ku saabsan la kulanka qoyska",
            hidden: "Sawir qarsoon oo profile ah",
        },
    },
};

export default {
    // A page of its own, without the music site's sidebar and player
    layout: (_, page) => page,
    components: { LuuqaLogo, StoreButtons },
    props: {
        lang: { type: String, default: "en" },
        faq: { type: Array, default: () => [] },
        stores: { type: Object, required: true },
    },
    data() {
        return {
            cdn: "https://cdn.nomadlyrics.com/marketing/luuqa/v4",
            // The same four actions as under a message in the app
            reactionIcons: [
                { name: "like", color: "#24120A", paths: ["M7 10v12", "M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"] },
                { name: "dislike", color: "#24120A", paths: ["M17 14V2", "M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"] },
                { name: "love", color: "#F24E0F", paths: ["M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"] },
                { name: "report", color: "#7A675D", paths: ["M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z", "M4 22v-7"] },
            ],
            languages: [
                { code: "en", label: "English", href: "/luuqa" },
                { code: "so", label: "Soomaali", href: "/luuqa/so" },
            ],
        };
    },
    mounted() {
        this.centerPeople();
    },
    methods: {
        // On phones the banner scrolls sideways, starting from the middle
        centerPeople() {
            const row = this.$refs.people;
            if (row) row.scrollLeft = (row.scrollWidth - row.clientWidth) / 2;
        },
    },
    computed: {
        t() {
            return WORDS[this.lang] ?? WORDS.en;
        },
    },
};
</script>
